import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Layer Control Context
 *
 * Provides React state management for Leaflet map layers.
 * Replaces the native L.control.layers with a React-based system.
 *
 * Features:
 * - Base layer management (mutually exclusive)
 * - Overlay layer management (toggle on/off)
 * - Layer visibility preferences (which layers appear in the control)
 * - Synchronisation with Leaflet map
 * - Persistence of active layers via URL parameters
 */
import { createContext, useContext, useState, useCallback, useEffect, useMemo, } from 'react';
import { isLayerVisibleInSelector, getLayerKey, } from '../utils/layerControlBridge';
const LayerControlContext = createContext(null);
export const LayerControlProvider = ({ children }) => {
    const [baseLayers, setBaseLayers] = useState([]);
    const [activeBaseLayerId, setActiveBaseLayerId] = useState(null);
    const [overlayLayers, setOverlayLayers] = useState([]);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isInitialised, setIsInitialised] = useState(false);
    // Get the Leaflet map from window
    const getMap = useCallback(() => {
        return window.map || null;
    }, []);
    // ───────────────────────────────────────────────────────────────────────────
    // Base Layer Management
    // ───────────────────────────────────────────────────────────────────────────
    const addBaseLayer = useCallback((id, name, layer, definition) => {
        setBaseLayers(prev => {
            // Check if already exists
            if (prev.some(l => l.id === id)) {
                return prev;
            }
            return [...prev, { id, name, layer, definition }];
        });
    }, []);
    const setActiveBaseLayer = useCallback((layerId) => {
        const map = getMap();
        if (!map) {
            console.warn('[LayerControl] No map available');
            return;
        }
        setBaseLayers(currentLayers => {
            // Remove all base layers from map
            currentLayers.forEach(entry => {
                if (map.hasLayer(entry.layer)) {
                    map.removeLayer(entry.layer);
                }
            });
            // Add the selected base layer
            const selectedEntry = currentLayers.find(l => l.id === layerId);
            if (selectedEntry && selectedEntry.layer.addTo) {
                selectedEntry.layer.addTo(map);
                console.log(`[LayerControl] Activated base layer: ${selectedEntry.name}`);
            }
            return currentLayers;
        });
        setActiveBaseLayerId(layerId);
    }, [getMap]);
    // ───────────────────────────────────────────────────────────────────────────
    // Overlay Layer Management
    // ───────────────────────────────────────────────────────────────────────────
    const addOverlayLayer = useCallback((id, name, layer, definition) => {
        const map = getMap();
        setOverlayLayers(prev => {
            // Check if already exists
            if (prev.some(l => l.id === id)) {
                return prev;
            }
            const displayName = definition.display_name || definition.name || name;
            const group = definition.groups?.[0] || 'Other';
            // Check if layer is already on the map (e.g., added by legacy code in main.js)
            // This ensures React state stays in sync with actual map state
            const isAlreadyOnMap = map ? map.hasLayer(layer) : false;
            return [...prev, {
                    id,
                    name,
                    displayName,
                    layer,
                    definition,
                    group,
                    isActive: isAlreadyOnMap,
                }];
        });
    }, [getMap]);
    const removeOverlayLayer = useCallback((layerId) => {
        const map = getMap();
        setOverlayLayers(prev => {
            const entry = prev.find(l => l.id === layerId);
            if (entry && map && map.hasLayer(entry.layer)) {
                map.removeLayer(entry.layer);
            }
            return prev.filter(l => l.id !== layerId);
        });
    }, [getMap]);
    const toggleOverlayLayer = useCallback((layerId) => {
        const map = getMap();
        if (!map) {
            console.warn('[LayerControl] No map available');
            return;
        }
        setOverlayLayers(prev => {
            return prev.map(entry => {
                if (entry.id !== layerId)
                    return entry;
                const isCurrentlyActive = map.hasLayer(entry.layer);
                const layer = entry.layer;
                if (isCurrentlyActive) {
                    // Deactivating layer
                    map.removeLayer(entry.layer);
                    console.log(`[LayerControl] Deactivated overlay: ${entry.displayName}`);
                    // Unregister UPRN dataset from WmsFeatureInfoHandler
                    if (layer._isUprnDataset && layer._uprnDatasetLayerId) {
                        const wmsHandler = window.wmsFeatureInfoHandler;
                        if (wmsHandler) {
                            wmsHandler.unregisterLayer(layer._uprnDatasetLayerId);
                            console.log(`[LayerControl] Unregistered UPRN dataset from WmsFeatureInfoHandler: ${layer._uprnDatasetLayerId}`);
                        }
                    }
                }
                else if (entry.layer.addTo) {
                    // Activating layer
                    entry.layer.addTo(map);
                    console.log(`[LayerControl] Activated overlay: ${entry.displayName}`);
                    // Register UPRN dataset with WmsFeatureInfoHandler
                    if (layer._isUprnDataset && layer._uprnDatasetId) {
                        const adapter = window.UprnDatasetLayerAdapter;
                        if (adapter) {
                            const displayName = layer._layerDefinition?.display_name ||
                                layer._layerDefinition?.name ||
                                entry.displayName;
                            adapter.addClickHandler(layer, map, layer._uprnDatasetId, displayName);
                            console.log(`[LayerControl] Registered UPRN dataset with WmsFeatureInfoHandler: ${layer._uprnDatasetId}`);
                        }
                    }
                }
                return { ...entry, isActive: !isCurrentlyActive };
            });
        });
    }, [getMap]);
    const isOverlayActive = useCallback((layerId) => {
        const entry = overlayLayers.find(l => l.id === layerId);
        if (!entry)
            return false;
        const map = getMap();
        if (!map)
            return entry.isActive;
        return map.hasLayer(entry.layer);
    }, [overlayLayers, getMap]);
    // ───────────────────────────────────────────────────────────────────────────
    // Visibility in Selector
    // ───────────────────────────────────────────────────────────────────────────
    const isLayerVisibleInControl = useCallback((definition) => {
        return isLayerVisibleInSelector(definition);
    }, []);
    // ───────────────────────────────────────────────────────────────────────────
    // Group overlays by their group
    // ───────────────────────────────────────────────────────────────────────────
    const overlayLayersByGroup = useMemo(() => {
        const groups = {};
        overlayLayers.forEach(entry => {
            // Skip layers hidden from selector
            if (!isLayerVisibleInControl(entry.definition)) {
                return;
            }
            const groupName = entry.group || 'Other';
            if (!groups[groupName]) {
                groups[groupName] = [];
            }
            groups[groupName].push(entry);
        });
        // Convert to array and sort
        return Object.entries(groups)
            .map(([id, layers]) => ({
            id,
            name: id.charAt(0).toUpperCase() + id.slice(1).replace(/_/g, ' '),
            layers,
        }))
            .sort((a, b) => {
            // Put 'main' first, then alphabetical
            if (a.id === 'main')
                return -1;
            if (b.id === 'main')
                return 1;
            return a.name.localeCompare(b.name);
        });
    }, [overlayLayers, isLayerVisibleInControl]);
    // ───────────────────────────────────────────────────────────────────────────
    // Toggle expanded state
    // ───────────────────────────────────────────────────────────────────────────
    const toggleExpanded = useCallback(() => {
        setIsExpanded(prev => !prev);
    }, []);
    // ───────────────────────────────────────────────────────────────────────────
    // Initialisation
    // ───────────────────────────────────────────────────────────────────────────
    const initialise = useCallback(async (catalog) => {
        console.log('[LayerControl] Initialising with catalog...');
        const LayerAdapter = window.LayerAdapter;
        if (!LayerAdapter) {
            console.error('[LayerControl] LayerAdapter not available');
            return;
        }
        // Load base layers
        const baseLayerConfigs = [
            { id: 'os_maps', name: 'OS Maps' },
            { id: 'osm_maps', name: 'OSM Maps' },
            { id: 'googleStreets', name: 'Google Streets' },
            { id: 'googleHybrid', name: 'Google Hybrid' },
        ];
        for (const config of baseLayerConfigs) {
            const layer = await LayerAdapter.getLayer(config.id);
            if (layer) {
                addBaseLayer(config.id, config.name, layer, layer._layerDefinition);
            }
        }
        // Set OS Maps as default
        setActiveBaseLayerId('os_maps');
        // Load overlay layers from groups
        const groups = ['main', 'rural', 'openreach', 'market', 'uprn_datasets'];
        for (const groupId of groups) {
            const groupLayers = await LayerAdapter.getLayersByGroup(groupId);
            for (const [name, layer] of Object.entries(groupLayers)) {
                const leafletLayer = layer;
                const definition = leafletLayer._layerDefinition;
                if (definition) {
                    const layerId = getLayerKey(definition) || name;
                    addOverlayLayer(layerId, name, leafletLayer, definition);
                }
            }
        }
        setIsInitialised(true);
        console.log('[LayerControl] Initialisation complete');
    }, [addBaseLayer, addOverlayLayer]);
    // ───────────────────────────────────────────────────────────────────────────
    // Refresh layer control
    // ───────────────────────────────────────────────────────────────────────────
    const refreshLayerControl = useCallback(() => {
        console.log('[LayerControl] Refreshing layer control');
        // Force re-render by updating overlay layers to refresh visibility checks
        setOverlayLayers(prev => [...prev]);
    }, []);
    // Listen for preference changes
    useEffect(() => {
        const handlePreferencesChanged = () => {
            refreshLayerControl();
        };
        window.addEventListener('layerSelectorPreferencesChanged', handlePreferencesChanged);
        return () => {
            window.removeEventListener('layerSelectorPreferencesChanged', handlePreferencesChanged);
        };
    }, [refreshLayerControl]);
    useEffect(() => {
        const handleRegisterOverlay = (event) => {
            const customEvent = event;
            const detail = customEvent.detail;
            if (!detail?.layer || !detail?.definition) {
                return;
            }
            const layerId = detail.id || getLayerKey(detail.definition) || detail.definition.name;
            if (!layerId) {
                console.warn('[LayerControl] Ignoring overlay registration without id');
                return;
            }
            const layerName = detail.name || detail.definition.display_name || detail.definition.name || layerId;
            addOverlayLayer(layerId, layerName, detail.layer, detail.definition);
        };
        window.addEventListener('registerOverlayLayer', handleRegisterOverlay);
        return () => {
            window.removeEventListener('registerOverlayLayer', handleRegisterOverlay);
        };
    }, [addOverlayLayer]);
    // ───────────────────────────────────────────────────────────────────────────
    // Context value
    // ───────────────────────────────────────────────────────────────────────────
    const value = useMemo(() => ({
        baseLayers,
        activeBaseLayerId,
        setActiveBaseLayer,
        addBaseLayer,
        overlayLayers,
        overlayLayersByGroup,
        toggleOverlayLayer,
        isOverlayActive,
        addOverlayLayer,
        removeOverlayLayer,
        isLayerVisibleInControl,
        isExpanded,
        setIsExpanded,
        toggleExpanded,
        isInitialised,
        initialise,
        refreshLayerControl,
    }), [
        baseLayers,
        activeBaseLayerId,
        setActiveBaseLayer,
        addBaseLayer,
        overlayLayers,
        overlayLayersByGroup,
        toggleOverlayLayer,
        isOverlayActive,
        addOverlayLayer,
        removeOverlayLayer,
        isLayerVisibleInControl,
        isExpanded,
        toggleExpanded,
        isInitialised,
        initialise,
        refreshLayerControl,
    ]);
    return (_jsx(LayerControlContext.Provider, { value: value, children: children }));
};
// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────
export const useLayerControl = () => {
    const context = useContext(LayerControlContext);
    if (!context) {
        throw new Error('useLayerControl must be used within a LayerControlProvider');
    }
    return context;
};
